import { getOnboardingDemoFilesAndPromptsForUser } from "@mirage/growth/onboarding/assistant-demo/demo-contents";
import { useOnboardingSurveys } from "@mirage/growth/onboarding/hooks/useOnboardingSurveys";
import i18n, { getAccountLocale } from "@mirage/translations";
import { useEffect, useMemo, useState } from "react";

import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import type {
  ProfilingQuestionIndustryAnswer,
  ProfilingQuestionKindOfWorkAnswer,
} from "@mirage/service-onboarding-surveys/service/onboarding-surveys/self-serve-profiling";
import type { OnboardingSurveyAnswer } from "@mirage/service-onboarding-surveys/service/types";

export type DemoPromptSuggestion = QuerySuggestionResult & {
  promptType: "brainstorm" | "analyze" | "summarize";
};

const getKindOfWork = (responses: OnboardingSurveyAnswer[]) => {
  return responses?.find(
    (response) => response.surveyQuestionId === "KIND_OF_WORK_V1",
  )?.surveyAnswer as ProfilingQuestionKindOfWorkAnswer;
};

const getIndustry = (responses: OnboardingSurveyAnswer[]) => {
  return responses?.find(
    (response) => response.surveyQuestionId === "INDUSTRY_V1",
  )?.surveyAnswer as ProfilingQuestionIndustryAnswer;
};

const ACCEPTED_LOCALES = ["en", "en-us"];

export const useDemoQuerySuggestions = () => {
  const [demoSuggestions, setDemoSuggestions] = useState<
    DemoPromptSuggestion[]
  >([]);
  const [locale, setLocale] = useState<string>("en");

  const { surveyResponses: adminSurveyResponses } =
    useOnboardingSurveys("ADMIN_PROFILING_V1");

  const { surveyResponses: teamMemberSurveyResponses } = useOnboardingSurveys(
    "TEAM_MEMBER_PROFILING_V1",
  );

  const industry = getIndustry(adminSurveyResponses);

  const role =
    getKindOfWork(adminSurveyResponses) ||
    getKindOfWork(teamMemberSurveyResponses);

  const demoAndPrompts = useMemo(() => {
    return getOnboardingDemoFilesAndPromptsForUser({ industry, role });
  }, [industry, role]);

  useEffect(() => {
    const doSetLocale = async () => {
      try {
        const locale = await getAccountLocale();
        setLocale(locale);
      } catch {
        setLocale("");
      }
    };
    doSetLocale();
  }, []);

  useEffect(() => {
    if (!ACCEPTED_LOCALES.includes(locale.toLowerCase())) {
      // If the locale is not English, we don't show demo suggestions
      setDemoSuggestions([]);
      return;
    }

    const suggestions =
      demoAndPrompts?.map((demo) => {
        let text = "";
        let uuid = "";
        switch (demo.prompt) {
          case "brainstorm":
            uuid = "query_suggestions_brainstorm_ideas";
            text = i18n.t("query_suggestions_brainstorm_ideas");
            break;
          case "analyze":
            uuid = "query_suggestions_analyze_report";
            text = i18n.t("query_suggestions_analyze_report");
            break;
          case "summarize":
            uuid = "query_suggestions_summarize_file";
            text = i18n.t("query_suggestions_summarize_file");
            break;
        }
        return {
          uuid,
          text,
          docIds: [demo.fileName],
          promptType: demo.prompt,
        };
      }) ?? [];

    setDemoSuggestions(suggestions);
  }, [demoAndPrompts, locale]);

  return { demoSuggestions };
};
